import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface ConvertInstallment {
  installment_number: number;
  amount: number;
  due_date: string; // 'YYYY-MM-DD'
}

interface ConvertServiceOrderParams {
  serviceOrderId: string;
  salespersonId: string;
  paymentMethod: string;
  installments: ConvertInstallment[];
}

function toastForError(err: unknown) {
  if (err && typeof err === 'object' && 'message' in err) {
    const msg = (err as { message: string }).message ?? '';

    if (msg.includes('already_billed')) {
      toast.error('Esta OS já foi convertida em venda.');
      return;
    }
    if (msg.includes('not_ready')) {
      toast.error(
        'A OS precisa estar aprovada ou em execução para ser convertida.',
      );
      return;
    }
    if (
      msg.includes('42501') ||
      msg.includes('insufficient_privilege') ||
      msg.includes('Permissão insuficiente')
    ) {
      toast.error('Você não tem permissão para converter ordens de serviço.');
      return;
    }
  }
  toast.error('Erro ao converter OS em venda. Tente novamente.');
}

export function useConvertServiceOrderToSale() {
  const queryClient = useQueryClient();
  const { company } = useAuth();

  return useMutation({
    mutationFn: async (params: ConvertServiceOrderParams): Promise<string> => {
      if (!company?.id) throw new Error('Autenticação necessária');

      const { data, error } = await supabase.rpc('convert_service_order_to_sale', {
        p_service_order_id: params.serviceOrderId,
        p_company_id:       company.id,
        p_salesperson_id:   params.salespersonId,
        p_payment_method:   params.paymentMethod,
        p_installments:     params.installments as unknown as string,
      });

      if (error) throw error;
      return data as string;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['service-order', variables.serviceOrderId],
      });
      queryClient.invalidateQueries({
        queryKey: ['service-orders'],
      });
      toast.success('OS convertida em venda com sucesso!');
    },
    onError: toastForError,
  });
}
